// The scrolling area under the bars, which doubles as the place files are dropped. It only lights
// up for a drag that is carrying files — dragging text or a link across it does nothing — and not
// at all without somewhere for the files to go. Items dragged within the drive are being moved, not
// uploaded, even where the browser offers a picture among them as a file.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useState} from 'react'

import UploadIcon from '@mui/icons-material/CloudUploadOutlined'

import {carriesItems} from '../../lib/hooks/useItemDrop'

import './dropZone.scss'

const carriesFiles = event => Array.from(event.dataTransfer?.types || []).includes('Files') && !carriesItems(event)

// A dropped directory arrives as a File with nothing readable in it, and sending one would fail
// at storage. Folders come in through Upload folder instead.
const droppedFiles = dataTransfer => {
  if (!dataTransfer.items) return Array.from(dataTransfer.files)

  return Array.from(dataTransfer.items)
    .filter(item => item.kind === 'file' && !item.webkitGetAsEntry?.()?.isDirectory)
    .map(item => item.getAsFile())
    .filter(Boolean)
}

const DropZone = ({onDrop, children}) => {
  const [hovering, setHovering] = useState(false)

  const handleDragOver = event => {
    if (!onDrop || !carriesFiles(event)) return

    event.preventDefault()
    setHovering(true)
  }

  // Moving between the zone's own children fires a leave for the one left behind.
  const handleDragLeave = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setHovering(false)
  }

  const handleDrop = event => {
    if (!onDrop || !carriesFiles(event)) return

    event.preventDefault()
    setHovering(false)
    onDrop(droppedFiles(event.dataTransfer))
  }

  return (
    <div
      className='drop-zone'
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {children}
      <div className={classnames('drop-overlay', {on: hovering})} aria-hidden={!hovering}>
        <div className='drop-message'>
          <UploadIcon className='drop-glyph' />
          <span>Drop to upload</span>
          <span className='drop-target'>this folder</span>
        </div>
      </div>
    </div>
  )
}

DropZone.propTypes = {
  onDrop: PropTypes.func,
  children: PropTypes.node.isRequired
}

export default DropZone
